'use client';

import { Service } from '@/util/types/serverTypes';
import { Chip, Listbox, ListboxItem, Selection } from '@heroui/react';
import IconDisplay from '@/components/ui/IconDisplay';

type ServiceListboxProps = {
  services: Service[];
  selectedKeys: Selection;
  onSelectionChange: (keys: Selection) => void;
  selectionMode?: 'single' | 'multiple';
  disabledKeys?: Selection;
  ariaLabel?: string;
  className?: string;
  iconBackgroundColor?: string;
  iconTextColor?: string;
  priceFormat?: 'starting' | 'addon';
  showPrice?: boolean;
  disallowEmptySelection?: boolean;
  /** Map of service IDs to badge text (e.g., "In Bundle") */
  badgeMap?: Record<string, string>;
};

export default function ServiceListbox({
  services,
  selectedKeys,
  onSelectionChange,
  selectionMode = 'single',
  disabledKeys,
  ariaLabel = 'Service Selection',
  className = 'rounded-xl border bg-white p-4',
  iconBackgroundColor,
  iconTextColor,
  showPrice = false,
  disallowEmptySelection = false,
  badgeMap,
}: ServiceListboxProps) {
  return (
    <Listbox
      aria-label={ariaLabel}
      variant='flat'
      selectionMode={selectionMode}
      selectedKeys={selectedKeys}
      disabledKeys={disabledKeys}
      onSelectionChange={onSelectionChange}
      disallowEmptySelection={disallowEmptySelection}
      className={className}
    >
      {services.map((service) => {
        const isDisabled = disabledKeys
          ? disabledKeys === 'all' || disabledKeys.has(service._id)
          : false;
        const badgeText = badgeMap?.[service._id];

        const iconBgColor = iconBackgroundColor || 'rgba(0, 111, 238, 0.4)';
        const iconColor = iconTextColor || '#006FEE';

        return (
          <ListboxItem
            key={service._id}
            description={service.description}
            className='py-2'
            classNames={{
              description: 'whitespace-normal',
            }}
            textValue={service.name}
            endContent={
              badgeText && (
                <Chip
                  size='sm'
                  variant='flat'
                  color='warning'
                  className='shrink-0 text-[10px]! uppercase font-bold!'
                  classNames={{
                    content: 'text-[10px]! uppercase font-bold!',
                  }}
                >
                  {badgeText}
                </Chip>
              )
            }
            startContent={
              <div
                className={`min-h-8 flex min-w-8 items-center justify-center rounded-lg ${
                  isDisabled ? 'bg-gray-100 opacity-50' : ''
                }`}
                style={
                  !isDisabled
                    ? {
                        backgroundColor: iconBgColor,
                        color: iconColor,
                      }
                    : undefined
                }
              >
                <IconDisplay
                  iconName={service.icon}
                  size={20}
                  defaultIcon='FaScrewdriverWrench'
                  className={isDisabled ? 'text-gray-400' : 'text-lg'}
                />
              </div>
            }
          >
            <div className='flex items-center justify-between gap-2'>
              <span className='font-semibold'>{service.name}</span>
              {showPrice && service.fee > 0 && (
                <span className='text-xs font-normal text-green-600'>
                  Starting At ${service.fee}
                </span>
              )}
            </div>
          </ListboxItem>
        );
      })}
    </Listbox>
  );
}
